// TeacherDashboard.js
import React, { useEffect, useState } from "react";
import { auth, db } from "../services/firebase";
import { ref, get, onValue } from "firebase/database";
import { useNavigate } from "react-router-dom";
import AdminAssessmentUpload from "../components/AdminAssessmentUpload";
import StudentIDGenerator from "../components/StudentIDGenerator";
import TeacherStudentsView from "../components/TeacherStudentsView";
import TeacherResultsView from "../components/TeacherResultsView";
import TeacherLiveMonitor from "../components/TeacherLiveMonitor";

import {
  FaChalkboardTeacher,
  FaBook,
  FaUsers,
  FaUpload,
  FaChartBar,
  FaCog,
  FaSignOutAlt,
  FaHome,
  FaPlusCircle,
  FaBars,
} from "react-icons/fa";
import { Tooltip } from "react-tooltip"; // 👉 npm i react-tooltip
import "./TeacherDashboard.css";

function TeacherDashboard() {
  const [teacher, setTeacher] = useState(null);
  const [quizzes, setQuizzes] = useState({});
  const [view, setView] = useState("home");
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [studentsCount, setStudentsCount] = useState(0);
  const [resultsCount, setResultsCount] = useState(0);
  const navigate = useNavigate();

  // Load teacher + quizzes
  useEffect(() => {
    const fetchData = async () => {
      if (!auth.currentUser) {
        navigate("/teacher-login");
        return;
      }

      const teacherRef = ref(db, "teacherRegistrations/" + auth.currentUser.uid);
      const snapshot = await get(teacherRef);
      if (snapshot.exists()) {
        setTeacher(snapshot.val());
      }

      const quizRef = ref(db, "quizzes/");
      onValue(quizRef, (snap) => {
        setQuizzes(snap.val() || {});
      });

      // Load student count
      const credsRef = ref(db, "studentCredentials");
      onValue(credsRef, (snap) => {
        const creds = snap.val() || {};
        const count = Object.values(creds).filter(
          (c) => c.createdBy === auth.currentUser.uid || !c.createdBy
        ).length;
        setStudentsCount(count);
      });

      // Load results count
      const resultsRef = ref(db, "studentResults");
      onValue(resultsRef, (snap) => {
        const results = snap.val() || {};
        const count = Object.values(results).length;
        setResultsCount(count);
      });

      setLoading(false);
    };

    fetchData();
  }, [navigate]);

  const logout = () => {
    auth.signOut();
    localStorage.removeItem("teacher");
    navigate("/teacher-login");
  };

  if (loading) return <p>Loading dashboard...</p>;
  if (!teacher) return <p>No teacher data found. Please login again.</p>;

  // Stats
  const uploadedQuizzes = Object.entries(quizzes)
    .filter(([_, q]) => q.createdBy === auth.currentUser.uid)
    .map(([id, q]) => ({ id, ...q }));

  return (
    <div className="tds-dashboard">
      {/* Sidebar */}
      <aside
        className={`tds-sidebar ${sidebarOpen ? "tds-expanded" : "tds-collapsed"}`}
      >
        <div className="tds-sidebar-header">
          <FaChalkboardTeacher className="tds-logo-icon" onClick={() => setSidebarOpen(!sidebarOpen)} />
          {sidebarOpen && <h2>BodhAI Teacher</h2>}
        </div>

        <nav>
          <button data-tooltip-id="tt-home" onClick={() => setView("home")}>
            <FaHome /> {sidebarOpen && "Home"}
          </button>
          <Tooltip id="tt-home" content="Home" place="right" />

          <button data-tooltip-id="tt-myquizzes" onClick={() => setView("myQuizzes")}>
            <FaBook /> {sidebarOpen && "My Quizzes"}
          </button>
          <Tooltip id="tt-myquizzes" content="My Quizzes" place="right" />

          <button data-tooltip-id="tt-upload" onClick={() => setView("AdminUpload")}>
            <FaUpload /> {sidebarOpen && "Upload Quiz"}
          </button>
          <Tooltip id="tt-upload" content="Upload Quiz" place="right" />

          <button data-tooltip-id="tt-results" onClick={() => setView("results")}>
            <FaChartBar /> {sidebarOpen && "Results"}
          </button>
          <Tooltip id="tt-results" content="Results" place="right" />

          <button data-tooltip-id="tt-students" onClick={() => setView("students")}>
            <FaUsers /> {sidebarOpen && "Students"}
          </button>
          <Tooltip id="tt-students" content="Students" place="right" />

          <button data-tooltip-id="tt-genrateidpass" onClick={() => setView("Genrate IDPASS")}>
            <FaUsers /> {sidebarOpen && "Generate IDPASS"}  
          </button>
          <Tooltip id="tt-genrateidpass" content="Generate IDPASS" place="right" />

          <button data-tooltip-id="tt-livemonitor" onClick={() => setView("LiveMonitor")}>
            <FaUsers /> {sidebarOpen && "Live Monitor"}  
          </button>
          <Tooltip id="tt-livemonitor" content="Live Monitor" place="right" />

          {/* Settings Button */ }
          <button data-tooltip-id="tt-settings" onClick={() => setView("settings")}>
            <FaCog /> {sidebarOpen && "Settings"}
          </button>
          <Tooltip id="tt-settings" content="Settings" place="right" />

          <button data-tooltip-id="tt-logout" onClick={logout}>
            <FaSignOutAlt /> {sidebarOpen && "Logout"}
          </button>
          <Tooltip id="tt-logout" content="Logout" place="right" />
        </nav>

        <div className="tds-sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
          <FaBars />
        </div>
      </aside>

      {/* Main Content */}
      <main className="tds-main-content">
        {view === "home" && (
          <div className="tds-home-view">
            <header className="tds-dashboard-header">
              <h2>Welcome, {teacher.firstName || teacher.name} 👋</h2>
              <p>Here’s an overview of your teaching activity.</p>
            </header>

            <div className="tds-stats-grid">
              <div className="tds-stat-card">
                <h3>{uploadedQuizzes.length}</h3>
                <p>Quizzes Uploaded</p>
              </div>
              <div className="tds-stat-card">
                <h3>{studentsCount}</h3>
                <p>Students Invited</p>
              </div>
              <div className="tds-stat-card">
                <h3>{resultsCount}</h3>
                <p>Results Submitted</p>
              </div>
              <div className="tds-stat-card">
                <h3>{resultsCount}</h3>
                <p>Total Attempts</p>
              </div>
            </div>

            <section className="tds-recent-section">
              <h3>📌 Recent Quizzes</h3>
              <ul>
                {uploadedQuizzes.slice(0, 5).map((quiz) => (
                  <li key={quiz.id}>{quiz.title || "Untitled Quiz"}</li>
                ))}
                {uploadedQuizzes.length === 0 && <p>No quizzes uploaded yet.</p>}
              </ul>
              <button
                className="tds-primary-btn"
                onClick={() => setView("AdminUpload")}
              >
                <FaPlusCircle /> Upload New Quiz
              </button>
            </section>
          </div>
        )}

        {view === "myQuizzes" && (
          <div className="tds-my-quizzes">
            <h2>My Quizzes</h2>
            <div className="tds-quiz-list">
              {uploadedQuizzes.map((quiz) => (
                <div className="tds-quiz-card" key={quiz.id}>
                  <h4>{quiz.title || "Untitled Quiz"}</h4>
                  <p>{quiz.date || "No date"}</p>
                </div>
              ))}
              {uploadedQuizzes.length === 0 && <p>No quizzes uploaded.</p>}
            </div>
          </div>
        )}

        {view === "AdminUpload" && (
          <div className="tds-upload-view">
            <AdminAssessmentUpload user={auth.currentUser} />
          </div>
        )}

        {view === "results" && (
          <div className="tds-results-view">
            <h2>Quiz Results</h2>
            <p>👉 Student results will be displayed here.</p>
            <p>👉 This feature is coming soon.</p>
          </div>
        )}

        {view === "students" && (
          <div className="tds-students-view">
            <h2>My Students</h2>
            <p>👉 Show student list, performance, and ranks here.</p>
          </div>
        )}
        {view === "Genrate IDPASS" && (
          <div className="tds-students-view">
            <h2>Generate Student IDPASS</h2>
            <p>👉 Use the <b>StudentIDGenerator</b> component here.</p>
            <p>👉 This will allow you to generate unique IDs and passwords for students.</p>
            <p>👉 Make sure to handle the generated IDs securely.</p>
            <p>👉 You can also provide options for downloading the IDs as CSV.</p>
            <StudentIDGenerator />
          </div>
        )}
        {view === "LiveMonitor" && (
          <TeacherLiveMonitor teacherId={auth.currentUser?.uid} />
        )} 

        {view === "settings" && (
          <div className="tds-settings-view">
            <h2>Settings</h2>
            <p>👉 Profile editing, preferences, and dark mode toggle can go here.</p>
          </div>
        )}
      </main>
    </div>
  );
}

export default TeacherDashboard;
